/**
 * Tiny module-level pub/sub channels for UI that derives from schedule state.
 *
 *  • scheduleMutated — fired by the clientV2 response interceptor on any
 *    successful write, so derived UI (header project-delta chip, F9 badge, …)
 *    can refetch without every mutation site wiring a callback. Coalesced so a
 *    burst of writes in one user action notifies once.
 *  • f9Refresh — "force-recompute the F9-sync badge", fired when the F9 Simulate
 *    flow opens. Distinct from scheduleMutated (no data changed): the badge's
 *    server cache is keyed on a data watermark, so an unchanged-data recompute
 *    needs an explicit force. Separate channel so it doesn't refetch other
 *    mutation-derived UI.
 *  • dataDateChanged — the project data date was set from the header picker.
 *    Views whose contents are defined relative to the data date (the overdue
 *    review) subscribe to refetch; a separate channel because scheduleMutated
 *    fires on every write and those views must not wipe in-flight staged edits
 *    for unrelated mutations.
 */
type Listener = () => void;

/** A minimal emitter; `coalesceMs` debounces a burst of emits into one notify. */
function createEmitter(coalesceMs?: number): {
  on: (listener: Listener) => () => void;
  emit: () => void;
} {
  const listeners = new Set<Listener>();
  let pending: ReturnType<typeof setTimeout> | null = null;
  const fire = () => listeners.forEach((cb) => cb());
  return {
    on(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    emit() {
      if (coalesceMs == null) {
        fire();
        return;
      }
      if (pending != null) return;
      pending = setTimeout(() => {
        pending = null;
        fire();
      }, coalesceMs);
    },
  };
}

const scheduleMutated = createEmitter(150);
const f9Refresh = createEmitter();
const dataDateChanged = createEmitter();

/** Subscribe to schedule mutations; returns an unsubscribe fn. */
export const onScheduleMutated = scheduleMutated.on;
/** Notify subscribers, coalescing a burst of writes into a single notification. */
export const emitScheduleMutated = scheduleMutated.emit;

/** Subscribe to F9-badge force-refresh; returns an unsubscribe fn. */
export const onF9Refresh = f9Refresh.on;
/** Ask the F9-sync badge to recompute now (cache-bypassing). */
export const emitF9Refresh = f9Refresh.emit;

/** Subscribe to data-date changes; returns an unsubscribe fn. */
export const onDataDateChanged = dataDateChanged.on;
/** Notify data-date-relative views that the project data date changed. */
export const emitDataDateChanged = dataDateChanged.emit;
